import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type { AddUserInput, UpdateUserInput } from '@/lib/api/domains/users';
import type { UserId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * Add-user mutation (F02 R16/R18). Mutate → invalidate: on success the entity's
 * user list is invalidated so the new person appears. A per-organization email
 * collision is a 409 the server returns with nothing created (AC-a094f421); the
 * mutation does NOT swallow it — the error propagates as an `ApiError` so the
 * dialog can surface the reason where the operator works and treat the save as
 * failed, never as success. When the collision is with a DEACTIVATED account
 * (R40), the dialog recovers the colliding profile from that same error
 * (`deactivatedCollisionFromError`) and offers reactivation.
 */
export function useAddUser(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddUserInput) => api.users.addUser(entityId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.users.listForEntity(entityId),
      });
    },
  });
}

/*
 * Update-user mutation (F02 R21, AC-cd9143ad; compound-write guarantee R34,
 * AC-e7ceefba/AC-76f5f026). The seam the user editor's Save invokes to change
 * an account against its current state. Mutate → invalidate: on success both
 * the entity's user list and the account's detail are invalidated so the list
 * and a reopened editor re-read what is now in force. No optimistic update —
 * like every write, mutate then invalidate (financial-adjacent admin write).
 * The error is NOT swallowed: a server refusal (the role↔org-kind pin, a failed
 * compound write) propagates as an `ApiError` the editor surfaces in-dialog, so
 * nothing is shown as saved when the server refused. The request targets the
 * account by id.
 */
export function useUpdateUser(entityId: string, id: UserId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserInput) =>
      api.users.updateUser(entityId, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.users.listForEntity(entityId),
      });
      void queryClient.invalidateQueries({
        queryKey: keys.users.detail(entityId, id),
      });
    },
  });
}

/*
 * Deactivate-user mutation (F02 R22). Marks the account inactive and KEEPS the
 * record (glossary "Deactivated"; AC-ecc1bd98 retention — a deactivate is never
 * a delete; the retention guarantee binds the Identity row, CR-E01-07). Mutate
 * → invalidate: on success both the entity's user list and the account's detail
 * are invalidated so the row shows inactive and a reopened editor re-reads the
 * current state. No optimistic update — like every write, mutate then
 * invalidate. The request targets the account by ID only; the person's name is
 * UI-only, never sent.
 *
 * The deactivate CONFIRMATION that names the person and states the effect before
 * this runs (AC-ce795f57) and the rule that it is offered only on an active
 * account (AC-74de7113) are the surface's (EntityUsersSection + the confirm
 * dialog); this is the data-layer seam they invoke on confirm.
 */
export function useDeactivateUser(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UserId) => api.users.deactivateUser(entityId, id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({
        queryKey: keys.users.listForEntity(entityId),
      });
      void queryClient.invalidateQueries({
        queryKey: keys.users.detail(entityId, id),
      });
    },
  });
}

/*
 * Reactivate-user mutation (F02 R40, AC-989dd3a3; R23 AC-25350d0f /
 * AC-fa0ba0de). The seam the add-user collision uses to OFFER bringing a
 * deactivated account back instead of creating a second one, and the seam the
 * user editor's named "Reactivate" affordance invokes (AC-25350d0f). Mutate →
 * invalidate: on success the entity's user list and the account's detail are
 * invalidated so the reactivated account shows active with the set it held
 * RESTORED (AC-fa0ba0de — the restored set is on the returned `User`, read back
 * on the re-read). The request targets the account by ID only — the person's
 * name is UI-only, never sent.
 *
 * DEPENDS re-check (BUILT ON ASSUMPTION CR-E01-10, confirm): that reactivation
 * restores the PRIOR set without anyone reassigning it is Identity's behaviour,
 * assumed here. The MSW handler stands in for it. If CR-E01-10 is declined,
 * re-check this seam and the handler — the restored-set assertion lives against
 * the mocked boundary. No optimistic update: like every write, mutate then
 * invalidate.
 */
export function useReactivateUser(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UserId) => api.users.reactivateUser(entityId, id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({
        queryKey: keys.users.listForEntity(entityId),
      });
      void queryClient.invalidateQueries({
        queryKey: keys.users.detail(entityId, id),
      });
    },
  });
}
